/**
 * Where a person goes: the intake without an account, sign-in, the onboarding
 * stepper, or the app.
 *
 * Pure, and free of React and native imports, so it is tested in Node
 * (`__tests__/session-gate.test.ts`). `session-gate.tsx` feeds it from the
 * session context, the device's memory of a sign-in, and the onboarding
 * query.
 *
 * ── NO ACCOUNT: THE INTAKE FIRST, UNLESS THIS DEVICE HAS SIGNED IN (`D262`)
 * A person with no session and no history on this device answers the intake
 * as a guest, and is asked for the e-mail at its end. A device that has had a
 * session — signed out on purpose, or whose session the server ended — opens
 * on sign-in instead, because the person there almost certainly has an
 * account. Either screen carries a door to the other, and the door the person
 * took outranks the default until a session exists.
 */
import {
  ONBOARDING_STEPS,
  type OnboardingProgress,
} from "@repo/contract/training";

export type GateDecision = "auth" | "guest" | "onboarding" | "app";

/** The door the person took on a screen without a session. */
export type GateDoor = "sign-in" | "intake";

export interface GateInput {
  /**
   * The door taken on the intake ("Já tenho conta") or on sign-in ("Ainda
   * não tenho conta"), or `null` when none was. Read only without a session.
   */
  readonly door?: GateDoor | null;
  /** `null` while the token store is still being read. */
  readonly hasSession: boolean | null;
  /**
   * The `GET /me/onboarding` answer: fresh, or restored from the persisted
   * cache on a cold start. `undefined` when there is none yet.
   */
  readonly onboarding: OnboardingProgress | undefined;
  /** The onboarding query failed and there is no cached answer to show. */
  readonly onboardingFailed?: boolean;
  /**
   * This device has held a session before (`D262`); `null` while that is
   * still being read. Read only without a session.
   */
  readonly signedInBefore?: boolean | null;
}

/**
 * `"pending"` is the decision NOT to decide yet: the token store has not
 * answered, a device without a session has not said whether it signed in
 * before, or a signed-in person's onboarding answer is neither cached nor
 * fetched. The gate paints nothing for it, because painting the sign-in
 * screen there would flash it at a person who is signed in.
 */
export function decideGate(input: GateInput): GateDecision | "pending" {
  if (input.hasSession === null) {
    return "pending";
  }
  if (!input.hasSession) {
    return withoutSession(input);
  }
  if (input.onboarding === undefined) {
    // No answer and none coming: let the person in rather than trap them on
    // a blank screen. The stepper is still reachable once the query answers.
    return input.onboardingFailed === true ? "app" : "pending";
  }
  return isOnboardingDone(input.onboarding) ? "app" : "onboarding";
}

function withoutSession(input: GateInput): GateDecision | "pending" {
  if (input.door === "sign-in") {
    return "auth";
  }
  if (input.door === "intake") {
    return "guest";
  }
  const before = input.signedInBefore ?? null;
  if (before === null) {
    return "pending";
  }
  return before ? "auth" : "guest";
}

/** Every step of `ONBOARDING_STEPS` whose answer is not stored yet. */
export function missingSteps(
  progress: OnboardingProgress
): OnboardingProgress["completed"] {
  const done = new Set(progress.completed);
  return ONBOARDING_STEPS.filter((step) => !done.has(step));
}

/** The account's intake is stored whole: its profile exists. */
export function isOnboardingDone(progress: OnboardingProgress): boolean {
  return missingSteps(progress).length === 0;
}

/**
 * What becomes of intake answers kept on this device before the account
 * existed (`D262`), once a session does:
 *
 * - `keep` — no session, or the account's progress is not known yet (offline
 *   with nothing cached). Nothing is decided on a guess: the answers wait;
 * - `adopt` — the account has not finished its intake. The answers become
 *   its own (`features/onboarding/hand-off.ts`) and the commit writes them.
 *   This is the brand-new account, and the account that signed up once and
 *   never finished;
 * - `discard` — the account finished its intake already. Its profile is
 *   NEVER overwritten by answers given on a device without it: they are
 *   dropped, and the person is told.
 *
 * `adopt` is exactly when the gate opens the stepper, which is the one place
 * a commit can run — so stale device answers cannot reach a finished profile
 * by any path.
 */
export type GuestAnswersFate = "keep" | "adopt" | "discard";

export function fateOfGuestAnswers(input: {
  readonly hasSession: boolean | null;
  readonly onboarding: OnboardingProgress | undefined;
}): GuestAnswersFate {
  if (input.hasSession !== true || input.onboarding === undefined) {
    return "keep";
  }
  return isOnboardingDone(input.onboarding) ? "discard" : "adopt";
}

/**
 * The progress of an account created by this very sign-in: nothing answered.
 * Seeded into the cache so a new person lands on the stepper without a
 * round trip whose answer is already known.
 */
export const NEW_ACCOUNT_PROGRESS: OnboardingProgress = {
  completed: [],
  done: false,
  next: ONBOARDING_STEPS[0],
};
